import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';
import CtaBand from '../components/CtaBand';
import { useContent } from '../content/ContentContext';
import { useUi } from '../i18n/ui';
import { renderMultiline } from '../utils/text';
import { publicApi } from '../api/insolita';
import { GalleryImage } from '../api/types';

const FALLBACK: Record<string, { title: string; file: string }[]> = {
  'partner-institutional': [
    { title: 'FMI', file: 'fmi.png' }, { title: 'Comune di Olbia', file: 'comune-olb.png' },
    { title: 'Regione Sardegna', file: 'Regione-sar.png' }, { title: 'Fondazione di Sardegna', file: 'Fondazione-sardegna.png' },
  ],
  'partner-tech': [
    { title: 'Rally Managing Group', file: 'MAN-R.png' }, { title: 'Motorally & Raid TT', file: 'MM.png' },
    { title: 'Moby', file: 'mby.png' }, { title: 'Tirrenia', file: 'Tirrenia.png' },
  ],
  sponsor: [
    { title: 'Hello Olbia', file: 'HO.png' }, { title: 'Amarcord', file: 'amarcord.png' },
    { title: 'Cantina Giba', file: 'giba.png' }, { title: 'Fort Box', file: 'fortbox.png' },
  ],
};

function LogoGrid({ items, category }: { items: GalleryImage[]; category: string }) {
  const cells = items.length
    ? items.map((g) => ({ key: String(g.id), src: g.url, alt: g.altText || g.title || '' }))
    : FALLBACK[category].map((f) => ({ key: f.file, src: `/img/${f.file}`, alt: f.title }));
  return (
    <div className="logos reveal">
      {cells.map((c) => <div key={c.key} className="cell"><img src={c.src} alt={c.alt} /></div>)}
    </div>
  );
}

const Gare: React.FC = () => {
  const { t, img } = useContent();
  const { tr } = useUi();
  const phone = t('contact.phone', '+393467814537');
  const [gallery, setGallery] = useState<Record<string, GalleryImage[]>>({});

  useEffect(() => {
    Promise.all(['partner-institutional', 'partner-tech', 'sponsor'].map((cat) =>
      publicApi.gallery(cat).then((imgs) => [cat, imgs] as const).catch(() => [cat, []] as const)
    )).then((entries) => setGallery(Object.fromEntries(entries)));
  }, []);

  return (
    <>
      <PageHero image={img('rally.hero.image', '/img/03-sandalion-1024x576.jpg')} crumb={tr('page.gare.title')} title={tr('page.gare.title')} />

      {/* INTRO — Sandalion Rally + Sardegna Rally Raid */}
      <section className="sec">
        <div className="wrap">
          <div className="split">
            <div className="reveal">
              <span className="eyebrow">{t('gare.intro.eyebrow', 'Sandalion Rally 2026 · Sardegna Rally Raid')}</span>
              <h2 className="h-sec" style={{ margin: '.35em 0 .6em' }}>{t('gare.intro.title', 'Le nostre gare')}</h2>
              <p className="lead" style={{ marginBottom: '1.1em' }}>
                {t('gare.description', "Da anni organizziamo manifestazioni di livello nazionale e internazionale, come il Sandalion Rally, il Sardegna Rally Raid e importanti eventi validi per i Campionati Italiani ed Europei, offrendo ai piloti percorsi tecnici, sicurezza e un'organizzazione di alto livello.")}
              </p>
              <p className="lead" style={{ marginBottom: '1.1em' }}>{t('gare.sandalion.text', 'Sandalion Rally — ottobre 2026: la sfida più attesa dell\'autunno sardo, tra prove speciali tecniche e paesaggi mozzafiato.')}</p>
              <p className="lead">{t('gare.srr.text', 'Sardegna Rally Raid — un evento unico che unisce moto e auto in una gara mista, con oltre 300 km di prove speciali nel nord-ovest dell\'isola.')}</p>
            </div>
            <div className="split__media reveal" data-d="1">
              <img src={img('rally.split.image', '/img/allenamento-motorally.jpg')} alt="Gare Insolita Sardegna" />
              <span className="frame" />
              <span className="tag">S2R · Sandalion 2026</span>
            </div>
          </div>
        </div>
      </section>

      <section className="sec sec--ink" style={{ paddingTop: 0 }}>
        <div className="wrap">
          <div className="stats reveal">
            <div className="stat"><b data-count={t('gare.stat1.value', '300')} data-suffix="+">0</b><span>{t('gare.stat1.label', 'Km di prove speciali')}</span></div>
            <div className="stat"><b data-count={t('gare.stat2.value', '2')}>0</b><span>{t('gare.stat2.label', "Grandi gare all'anno")}</span></div>
            <div className="stat"><b data-count={t('gare.stat3.value', '2')}>0</b><span>{t('gare.stat3.label', 'Campionati italiani')}</span></div>
          </div>
          <div style={{ textAlign: 'center', marginTop: 56 }} className="reveal">
            <p className="lead" style={{ margin: '0 auto 28px', textAlign: 'center', color: 'rgba(244,241,233,.8)' }}>
              {t('gare.sigma.text', 'A breve sarà possibile iscriversi alle gare tramite la piattaforma SIGMA di FMI. Per informazioni: 346 781 4537 — info@insolitasardegna.com')}
            </p>
            <Link to="/iscrizioni" className="btn btn-acid">{t('gare.sigma.cta', 'Convenzioni & iscrizioni')}</Link>
          </div>
        </div>
      </section>

      <section className="sec">
        <div className="wrap">
          <div className="sec-head reveal">
            <div><span className="eyebrow">{t('gare.partners.eyebrow', 'Con il sostegno di')}</span><h2 className="h-sec" style={{ marginTop: '.35em' }}>{renderMultiline(t('gare.partners.title', 'Partner\nistituzionali'))}</h2></div>
          </div>
          <LogoGrid items={gallery['partner-institutional'] || []} category="partner-institutional" />
          <h3 className="logos-label reveal">{t('gare.partners.tech', 'Partner tecnici')}</h3>
          <LogoGrid items={gallery['partner-tech'] || []} category="partner-tech" />
          <h3 className="logos-label reveal">{t('gare.partners.sponsor', 'Sponsor')}</h3>
          <LogoGrid items={gallery['sponsor'] || []} category="sponsor" />
        </div>
      </section>

      <CtaBand image={img('rally.cta.image', '/img/03-sandalion-1024x576.jpg')} eyebrow={t('gare.cta.eyebrow', 'Stagione 2026 · Olbia')} title={t('gare.cta.title', "Gas aperto,\nsguardo all'orizzonte")}
        text={t('gare.cta.text', 'Non perdere le nostre gare: dal Sandalion Rally al Sardegna Rally Raid, il grande fuoristrada ti aspetta in Sardegna.')}>
        <div className="band__phone"><span>{t('gare.cta.phonelabel', 'Info gare')}</span><a href={`tel:${phone}`}>(+39) {phone.replace('+39', '')}</a></div>
      </CtaBand>
    </>
  );
};

export default Gare;
